/* eslint-disable @typescript-eslint/indent */
import { FC, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import ErrorTemplate from '@/components/templates/Error';
import RaceCardTemplate from '@/components/templates/Velodrome/RaceCard';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import {
  GetRaceProgramQueryVariables,
  RaceProgram,
  useGetRaceProgramQuery,
  useGetRaceProgramsLazyQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { VelodromeRoutesProps } from '@/routes/Velodrome';
import { toHalfWidth } from '@/utility/utils';

const RaceCardPage: FC<VelodromeRoutesProps> = ({ velodrome }) => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const { heldDate } = useParams<{
    heldDate: string;
  }>();

  const raceProgramQuery = useGetRaceProgramQuery({
    variables: {
      input: {
        velodromeCode: velodrome.code,
        heldDate: heldDate ? dayjst(heldDate) : null,
      },
    } as GetRaceProgramQueryVariables,
    fetchPolicy: 'network-only',
  });

  const [loadRacePrograms, raceProgramsQuery] = useGetRaceProgramsLazyQuery();

  useEffect(() => {
    if (!raceProgramQuery.data) {
      return;
    }
    if (!raceProgramsQuery.data && !raceProgramsQuery.loading) {
      loadRacePrograms({
        variables: {
          input: {
            velodromeCode: velodrome.code,
            heldFirstDate: raceProgramQuery.data?.raceProgram.heldFirstDate,
          },
        },
      });
    }
    setBreadcrumbs([
      {
        name: `${velodrome.name}競輪`,
        to: `/${velodrome.romanName}`,
      },
      {
        name: toHalfWidth(
          `${
            raceProgramQuery.data.raceProgram.programName ||
            `${velodrome.name}競輪 ${raceProgramQuery.data.raceProgram.gradeName}`
          }（${raceProgramQuery.data.raceProgram.heldDate.format(
            'YYYY年M月D日',
          )}）`,
        ),
        to: '',
      },
    ]);
  }, [raceProgramQuery.data, raceProgramsQuery.data]);

  const title = `${velodrome.name}競輪 ${toHalfWidth(
    raceProgramQuery.data ? raceProgramQuery.data.raceProgram.programName : '',
  )}（${raceProgramQuery.data?.raceProgram.heldDate.format(
    'YYYY年M月D日',
  )}）全レース出走表`;

  const description = `${velodrome.name}競輪 ${toHalfWidth(
    raceProgramQuery.data ? raceProgramQuery.data.raceProgram.programName : '',
  )}の全レース出走表です。`;

  return (
    <>
      <Helmet
        meta={{
          title,
          description: `${description}%s`,
          keywords: `${title},%s`,
        }}
      />
      {raceProgramQuery.error || raceProgramsQuery.error ? (
        <ErrorTemplate
          apolloError={raceProgramQuery.error || raceProgramsQuery.error}
        />
      ) : (
        <>
          {raceProgramQuery.data && raceProgramsQuery.data && (
            <RaceCardTemplate
              raceProgram={raceProgramQuery.data.raceProgram as RaceProgram}
              racePrograms={
                raceProgramsQuery.data.racePrograms as RaceProgram[]
              }
            />
          )}
        </>
      )}
    </>
  );
};
export default RaceCardPage;
